import { useState } from 'react';

import { CHAT_MEDIA_TYPES, type ChatMediaType } from 'cpool-constants';
import { useTranslation } from 'react-i18next';
import { LuFileText, LuImage, LuMusic, LuVideo } from 'react-icons/lu';
import { toast } from 'react-toastify';

import Spinner from '~/components/icons/Spinner';
import {
    getChatDownloadRoute,
    resolveChatAttachmentDownloadName,
} from '~/features/whatsapp/utils/resolveChatAttachmentDownloadName';
import { downloadFile } from '~/utils/downloadFile';

const MEDIA_TYPE_ICONS: Record<ChatMediaType, React.ComponentType<{ size?: number; className?: string }>> = {
    [CHAT_MEDIA_TYPES.AUDIO]: LuMusic,
    [CHAT_MEDIA_TYPES.DOCUMENT]: LuFileText,
    [CHAT_MEDIA_TYPES.IMAGE]: LuImage,
    [CHAT_MEDIA_TYPES.STICKER]: LuImage,
    [CHAT_MEDIA_TYPES.VIDEO]: LuVideo,
};

type Props = {
    chatId: string;
    mediaFileName?: string | null;
    mediaKey: string;
    mediaType: ChatMediaType;
    name: string;
};

export const FileRow = ({ chatId, mediaFileName, mediaKey, mediaType, name }: Props) => {
    const { t } = useTranslation();
    const Icon = MEDIA_TYPE_ICONS[mediaType] ?? LuFileText;
    const resolvedName = resolveChatAttachmentDownloadName({ mediaFileName, mediaKey, mediaType, name });
    const [isDownloading, setIsDownloading] = useState(false);

    const handleDownload = async () => {
        setIsDownloading(true);
        try {
            await downloadFile({
                name: resolvedName,
                route: getChatDownloadRoute(chatId, mediaKey),
            });
        } catch {
            toast.error(t('WHATSAPP_CHAT.ATTACHMENT.DOWNLOAD_FAILED'));
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <button
            type="button"
            onClick={handleDownload}
            disabled={isDownloading}
            className="flex w-full items-center gap-3 rounded-md border border-outline-light bg-white p-3 text-left text-sm text-midnight hover:bg-surface-light disabled:opacity-50"
            data-testid="user-info-file-row"
        >
            <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-surface-light">
                {isDownloading ? <Spinner className="h-4 w-4" /> : <Icon size={18} className="text-grey" />}
            </span>
            <span className="truncate font-medium">{resolvedName}</span>
        </button>
    );
};
